import React, { useEffect, useMemo, useState, useRef, forwardRef } from 'react';
import { set, get, isEmpty, cloneDeep } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Accordion, Card, Button } from 'react-bootstrap';
import DatePicker, { CalendarContainer } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useProductCostingState } from '../ProductCostingContext';
import {
  convertSelectLabel,
  currencySymbols,
  methodShipment,
  checkIsNumber,
  restrictToTwoDecimals,
  staticDefaultCostingInfoColumnArray,
} from '../../../../Components/helper';
import { productInfoFieldPath, errorpath, commonPath } from '../fieldPath';
import useDetailedPageHook from '../Hooks/useDetailedPageHook';
import useShowHideSectionHooks from '../Hooks/useShowHideSectionHooks';
import { getProductCostingTemplateDetailsByIds, updateCurrencyinSupplierOverview } from '../store/productCosting.actions';
import type { ProductCostingDispatch, CostingTemplateDetail, TransactionMsg } from '../store/productCosting.types';
import type { LandedInfoStateEntry, ProductCostingState } from '../types';
import { useLengthLabel } from '../../../../hooks/useLengthLabel';
import type { LandedDistributionCentreState } from '../landed-combination.helpers';
import { CalendarIcon } from '../../../../bombiix-icon';
import { NewInput, NewSelectMultiSelect, NewTextArea } from '../../../../Components/PIM/common';
import { BombiixModal } from '../../../../Components/BombiixModal';
import { getSampleRefLabel } from '../../../Dashboard/TodoList/CostingTsx/costing-todo.helpers';

/** Datepicker trigger glyph, matched to the 28px field height this card uses. */
const ICON_SIZE_FIELD = 20;

/** A supplier as the costing header reads it. */
interface SupplierOption {
  _id?: string;
  name?: string;
  companies?: { currency?: string; agentFee?: unknown };
  [key: string]: unknown;
}

/** One factory offered under the selected supplier. */
interface FactoryOption {
  _id?: string;
  factoryName?: string;
  factoryInfo?: Array<{
    moq?: unknown;
    productType?: unknown[];
    category1?: unknown[];
    category2?: unknown[];
    category3?: unknown[];
    category4?: unknown[];
    [key: string]: unknown;
  }>;
  [key: string]: unknown;
}

/** One Sample Ref option: the id that is stored on the costing, and the sample type and number that are shown. */
interface SampleRefOption {
  _id?: string;
  sampleNumber?: string;
  label?: string;
  [key: string]: unknown;
}

/** An incoterm option — `{ _id, name }` with an active flag. */
interface NamedOption {
  _id?: string;
  name?: string;
  isActive?: boolean;
  isActiveIncoterms?: boolean;
  [key: string]: unknown;
}

/** A `{ label, value }` option as react-select consumes it. */
interface LabelValueOption {
  label?: string;
  value?: string;
  [key: string]: unknown;
}

/** One configurable Costing Information field on the template. */
interface CostingInfoField {
  _id?: string;
  title?: string;
  aliasDisplayName?: string;
  type?: string;
  isDefault?: boolean;
  [key: string]: unknown;
}

/** The costing template as this header reads it. */
interface TemplateWithCostingInfo {
  _id?: string;
  name?: string;
  isCostingInfoActive?: boolean;
  costingInfo?: CostingInfoField[];
  landedInfo?: unknown[];
  [key: string]: unknown;
}

/** One entry of the supplier-factory master. */
interface SupplierFactoryEntry {
  supplierId?: unknown;
  factories?: FactoryOption[];
  [key: string]: unknown;
}

/** The redux slices this header reads. */
interface ProductInfoRootState {
  newProductCostingData: {
    productColours?: unknown[];
    productSizes?: string[];
    costingTemplateDetail?: TemplateWithCostingInfo;
    isRefreshed?: boolean;
  };
  BOMConfigDetails: {
    suppliers?: SupplierOption[];
    productDetails?: {
      catOneId?: { _id?: string } | string | null;
      catTwoId?: { _id?: string } | string | null;
      catThreeId?: { _id?: string } | string | null;
      catFourId?: { _id?: string } | string | null;
      supplierType?: { _id?: string };
      supplierFactories?: string[];
      [key: string]: unknown;
    };
  };
  CostingTemplateData: { costingTemplateList?: TemplateWithCostingInfo[] };
  SystemMastersDetails: { activeIncotermsList?: NamedOption[] };
  BomLibraryDetails: { supplierFactories?: SupplierFactoryEntry[] };
}

export interface ProductInfoTableProps {
  CoastingAddEditRights: boolean;
}

export function ProductInfoTable({ CoastingAddEditRights }: ProductInfoTableProps): React.JSX.Element {
  const {
    productCostingstate,
    setProductCostingState,
    globalError,
    internalState,
    setInternalState,
    colSizeLengthToggleState,
    setActivateColSizeLengthToggle,
    landedInfoState,
    grandTotalState,
    additionalState,
    detailedCostingState,
    additionalComponentState,
    initialLandedInfoState,
    initialOptionsState,
    filteredDistributionCentres,
    landedInfoStates,
  } = useProductCostingState();
  const { devOrderId, productid } = useParams<{ devOrderId?: string; productid?: string }>();

  const {
    currencyOptionsArr,
    productInfo: {
      supplierId: supplierIdValue,
      currency,
      incoterms: incotermsValue,
      reference,
      shipmentMethod,
      targetCost,
      factoryId: factoryIdValue,
      moqNeeded,
    },
    sampleRef: sampleRefValue,
    productTemplate: productTemplateValue,
    averageCosting: { averageCostingActive },
    isGlobalUrl,
  } = productCostingstate;
  /* `ProductInfoState` types these four as ids because that is what the field NAMES say, but the
     costing document stores the populated objects — the same mismatch already documented in
     useDetailedPageHook for `supplierId`. Aliasing here keeps the component body unchanged. */
  const supplierId = supplierIdValue as unknown as SupplierOption | null;
  const incoterms = incotermsValue as unknown as NamedOption | null;
  const factoryId = factoryIdValue as unknown as FactoryOption | null;
  const sampleRef = sampleRefValue as SampleRefOption | null;
  const productTemplate = productTemplateValue as TemplateWithCostingInfo | null;
  const {
    detailedCosting: { detailedCostingActive },
  } = detailedCostingState;

  const {
    handleLandedInfoModule,
    handleAdditonalModule,
    handleAdditonalComponentModule,
    handleSpecificationRequirementModule,
    handleAverageCosting,
    handleQuantities,
    renderDetailedCostingArr,
    recalculateLandedInfoForIncoterm,
    applySupplierChangeToLandedInfo,
    getSavedLandedCombinationRows,
  } = useDetailedPageHook();
  /* The hook's own row/template interfaces are not exported; borrowing them from its signatures
     keeps these casts pinned to the real contract instead of a copy that could drift. */
  type LandedTemplateArg = Parameters<typeof handleLandedInfoModule>[0];
  type LandedGrandTotalArg = Parameters<typeof handleLandedInfoModule>[2];
  type IncotermArg = Parameters<typeof recalculateLandedInfoForIncoterm>[0];
  type BomListArg = Parameters<typeof renderDetailedCostingArr>[0];
  const { t } = useTranslation('common');
  const lengthLabel = useLengthLabel('Length');
  /* Typed dispatch, so the module's own thunks type-check at the call site. */
  const dispatch = useDispatch<ProductCostingDispatch>();
  const { colourWayToggle, sizeLengthToggle } = colSizeLengthToggleState;
  const landedInfoStateRef = useRef(landedInfoState);

  const { grandTotal } = grandTotalState;
  const grandTotalStateRef = useRef(grandTotal);

  const {
    newProductCostingData: { productColours, productSizes, costingTemplateDetail, isRefreshed },
    BOMConfigDetails: { suppliers, productDetails },
    CostingTemplateData: { costingTemplateList },
    SystemMastersDetails: { activeIncotermsList },
    BomLibraryDetails: { supplierFactories },
  } = useSelector((reduxState: ProductInfoRootState) => reduxState);

  const liveTemplateDetail =
    costingTemplateDetail && productTemplate && costingTemplateDetail._id === productTemplate._id ? costingTemplateDetail : productTemplate;
  const useLiveTemplate = !productid || isRefreshed;
  const isCostingInfoActive = liveTemplateDetail?.isCostingInfoActive ?? productTemplate?.isCostingInfoActive ?? false;
  const defaultCostingInfo = (liveTemplateDetail?.costingInfo ?? []).filter((item) => item.isDefault);
  const extraCostingInfo = ((useLiveTemplate ? liveTemplateDetail?.costingInfo : productTemplate?.costingInfo) ?? []).filter((item) => !item.isDefault);

  /**
   * Resolve a default Costing Information field's label by its canonical slot title, not by the
   * stored `position`. Default titles are immutable in the template builder (renames only touch
   * `aliasDisplayName`), so keying on title keeps each of the ten fixed slots aligned even when a
   * template's costingInfo has duplicated or shifted `position` values.
   * @param slot 0-based index into the canonical default field order.
   * @param fallback label to use when the field is absent or costing-info labels are off.
   */
  const getCostingInfoLabel = (slot: number, fallback: string): string => {
    if (!isCostingInfoActive) return fallback;
    const canonicalTitle = staticDefaultCostingInfoColumnArray[slot]?.title;
    const item = defaultCostingInfo.find((ci) => ci.title === canonicalTitle);
    return item ? ((item.aliasDisplayName || item.title) as string) : fallback;
  };

  const getCostingInfoPlaceholder = (slot: number, prefixKey: string, fallback: string): string => {
    if (!isCostingInfoActive) return fallback;
    const canonicalTitle = staticDefaultCostingInfoColumnArray[slot]?.title;
    const item = defaultCostingInfo.find((ci) => ci.title === canonicalTitle);
    const label = item ? item.aliasDisplayName || item.title : null;
    return label ? `${t(prefixKey)} ${label}` : fallback;
  };

  const { onLoadDisable, isDisabled, isSupplier, isSupplierView, markAsCompleteState, statusState } = useShowHideSectionHooks();

  const [comState, setState] = useState<{
    showSupplierChangePopup: boolean;
    supplierSelected: SupplierOption | null;
    showCurrencyChangepopup: boolean;
    selectedCurrency: string | null;
    selectReference: string;
    showHideAlertPopup: boolean;
    selectedTemplate: TemplateWithCostingInfo | null;
  }>({
    showSupplierChangePopup: false,
    supplierSelected: null,
    showCurrencyChangepopup: false,
    selectedCurrency: null,
    selectReference: '',
    showHideAlertPopup: false,
    selectedTemplate: null,
  });

  const handleCostingInfoChange = (id: string, value: unknown): void => {
    setInternalState((prev) => ({
      ...prev,
      costingInfoValues: { ...prev.costingInfoValues, [id]: value },
    }));
  };

  useEffect(() => {
    if (!productid && !isGlobalUrl && !shipmentMethod) {
      setProductCostingState((state) => {
        const copyState: ProductCostingState = cloneDeep(state);
        set(copyState, productInfoFieldPath().SELECTED_SHIPMENT_METHOD, 'Sea');
        return { ...state, ...copyState };
      });
    }
  }, [productid, shipmentMethod, setProductCostingState]);

  /**
   * Applies a Product Information dropdown selection and cascades it to dependent state.
   *
   * An incoterm change only drives the Licence/Likeness Royalty Fee columns, so existing landed rows
   * are recalculated in place rather than rebuilt from the costing template, which discarded every
   * entered value. The template build is kept only for the case where no landed rows exist yet.
   *
   * @param value the selected option.
   * @param path state path the selection is written to.
   * @param dependentPath optional companion path that receives the derived currency symbol.
   */
  const handleDropDown = async (value: unknown, path: string, dependentPath?: string): Promise<void> => {
    let moqVal: { moq?: unknown } | undefined;
    if (path?.includes('factoryId')) {
      const prodCat1 = productDetails?.catOneId || null;
      const prodCat2 = productDetails?.catTwoId || null;
      const prodCat3 = productDetails?.catThreeId || null;
      const prodCat4 = productDetails?.catFourId || null;
      const productType = productDetails?.supplierType?._id || null;
      moqVal = (value as FactoryOption)?.factoryInfo?.find((info) => {
        const productTypeOptions = isEmpty(info.productType) ? [null] : info?.productType;
        const category1Options = isEmpty(info?.category1) ? [null] : info?.category1;
        const category2Options = isEmpty(info?.category2) ? [null] : info?.category2;
        const category3Options = isEmpty(info?.category3) ? [null] : info?.category3;
        const category4Options = isEmpty(info?.category4) ? [null] : info?.category4;

        const matchProductType = productTypeOptions?.includes(productType);
        const matchCat1 = category1Options?.includes((prodCat1 as { _id?: string })?._id || prodCat1);
        const matchCat2 = category2Options?.includes((prodCat2 as { _id?: string })?._id || prodCat2);
        const matchCat3 = category3Options?.includes((prodCat3 as { _id?: string })?._id || prodCat3);
        const matchCat4 = category4Options?.includes((prodCat4 as { _id?: string })?._id || prodCat4);
        return matchProductType && matchCat1 && matchCat2 && matchCat3 && matchCat4;
      });
    }

    if (path.includes('currency')) {
      setState((prevState) => ({
        ...prevState,
        showCurrencyChangepopup: true,
        selectedCurrency: value as string,
      }));
    }

    setProductCostingState((state) => {
      const copyState: ProductCostingState = cloneDeep(state);
      if (path.includes('supplierId')) {
        set(copyState, path, value);
        set(copyState, productInfoFieldPath().SELECTED_CURRENCY, (value as SupplierOption)?.companies?.currency);
        set(copyState, productInfoFieldPath().SELECTED_FACTORY, null);
      } else if (path?.includes('factoryId')) {
        set(copyState, path, value);
        set(copyState, productInfoFieldPath().MOQ_NEEDED, moqVal?.moq);
      } else if (path.includes('currency')) {
        /* Deliberately not applied here — `updateCurrency` writes it once the user confirms.
           Applying it up front made the field render its own read-only branch (`currency` truthy
           swaps the dropdown for text), so dismissing the popup with Close left the unconfirmed
           currency on screen with no way back to the dropdown. The pending value lives on
           `comState.selectedCurrency` until then. */
      } else {
        set(copyState, path, value);
      }
      if (dependentPath) {
        set(copyState, dependentPath, (currencySymbols as Record<string, unknown>)[value as string]);
      }
      return { ...state, ...copyState };
    });

    if (path.includes('supplierId')) {
      await applySupplierChangeToLandedInfo(value as SupplierOption);
    }

    if (path?.includes('incoterms')) {
      const hasLandedRows = landedInfoStates?.some((dcState) => (dcState?.landedInfo?.landedInfoArr?.length ?? 0) > 0);
      if (hasLandedRows) {
        await recalculateLandedInfoForIncoterm(value as IncotermArg);
      } else if (productTemplate) {
        await handleLandedInformationChanges(productTemplate, value as IncotermArg);
      }
    }
  };

  const handleChange = async (value: unknown, path: string): Promise<void> => {
    const copyState = productCostingstate;
    set(copyState, path, value);
    setProductCostingState((state) => ({ ...state, ...copyState }));
  };

  const updateSupplier = async (): Promise<void> => {
    const copyState: ProductCostingState = cloneDeep(productCostingstate);
    if (detailedCostingActive) {
      if (supplierId && supplierId?._id !== comState?.supplierSelected?._id) {
        set(copyState, productInfoFieldPath().SELECTED_SUPPLIER, comState?.supplierSelected);
        set(copyState, productInfoFieldPath().SELECTED_CURRENCY, comState.supplierSelected?.companies?.currency);
        setProductCostingState((state) => ({ ...state, ...copyState }));
      } else {
        set(copyState, productInfoFieldPath().SELECTED_SUPPLIER, comState?.supplierSelected);
        set(copyState, productInfoFieldPath().SELECTED_CURRENCY, comState.supplierSelected?.companies?.currency);
      }
    } else {
      set(copyState, productInfoFieldPath().SELECTED_SUPPLIER, comState?.supplierSelected);
      set(copyState, productInfoFieldPath().SELECTED_CURRENCY, comState?.supplierSelected?.companies?.currency);
    }
    setState((prevState) => ({
      ...prevState,
      showSupplierChangePopup: false,
      supplierSelected: null,
    }));
    setProductCostingState((state) => ({ ...state, ...copyState }));
    await applySupplierChangeToLandedInfo(comState?.supplierSelected);
  };

  /* A no-op in the original: every call site guards on an existing error, but clearing it was never
     wired up here. Preserved rather than removed so the call sites stay identical. */
  const updateError = (_path: string): void => undefined;

  const onChangeSupplier = (selected: SupplierOption | null): void => {
    if (productid && selected?._id !== supplierId?._id && supplierId) {
      setState((prevState) => ({
        ...prevState,
        showSupplierChangePopup: true,
        supplierSelected: selected,
      }));
    } else {
      handleDropDown(selected, productInfoFieldPath().SELECTED_SUPPLIER);
    }
  };

  const updateCurrency = async (): Promise<void> => {
    const copyState = productCostingstate;

    const obj = {
      supplierId: supplierId?._id,
      currency: comState?.selectedCurrency,
    };
    const res = await dispatch(updateCurrencyinSupplierOverview(obj, t as unknown as TransactionMsg));
    if (res) {
      setState((prevState) => ({
        ...prevState,
        showCurrencyChangepopup: false,
        supplierSelected: null,
      }));
      set(copyState, productInfoFieldPath().SELECTED_CURRENCY, comState?.selectedCurrency);
      setProductCostingState((state) => ({ ...state, ...copyState }));
    } else {
      setState((prevState) => ({
        ...prevState,
        showCurrencyChangepopup: false,
        supplierSelected: null,
      }));
    }
  };

  const handleSelectSampleRef = async (selected: SampleRefOption | null): Promise<void> => {
    const copyState = productCostingstate;
    set(copyState, commonPath().SELECTED_SAMPLE_REF, selected);
    setProductCostingState((state) => ({ ...state, ...copyState }));
  };

  const openAlertModal = (selected: TemplateWithCostingInfo | null): void => {
    setState((prevState) => ({
      ...prevState,
      showHideAlertPopup: !prevState.showHideAlertPopup,
      selectedTemplate: selected,
    }));
  };

  const handleLandedInformationChanges = async (selected: TemplateWithCostingInfo | null, incotermVal?: IncotermArg): Promise<void> => {
    if (selected) {
      const res = await dispatch(getProductCostingTemplateDetailsByIds(selected?._id, devOrderId, { colourWayToggle, sizeLengthToggle }));
      if (res) {
        const dcList: LandedInfoStateEntry[] =
          landedInfoStates.length > 0
            ? landedInfoStates
            : filteredDistributionCentres.map(
                (dc) =>
                  ({
                    distributionCentre: dc,
                    landedInfo: { landedInfoColumnArr: [], landedInfoArr: [], currency: null, targetCost: '' },
                    landedExchangeRateInformation: {},
                  } as LandedInfoStateEntry),
              );
        if (dcList.length > 0) {
          for (let i = 0; i < dcList.length; i++) {
            await handleLandedInfoModule(
              res as LandedTemplateArg,
              dcList[i] as LandedInfoStateEntry,
              grandTotalStateRef?.current as LandedGrandTotalArg,
              incotermVal,
              i,
            );
          }
        } else {
          await handleLandedInfoModule(
            res as LandedTemplateArg,
            landedInfoState as unknown as LandedInfoStateEntry,
            grandTotalStateRef?.current as LandedGrandTotalArg,
            incotermVal,
          );
        }
      }
    }
  };

  useEffect(() => {
    landedInfoStateRef.current = landedInfoState;
  }, [landedInfoState]);

  useEffect(() => {
    grandTotalStateRef.current = grandTotal;
  }, [grandTotal]);

  /**
   * Applies a newly picked costing template to every dependent module.
   * `landedInformationColumns` is refreshed alongside `productTemplate` because it is a
   * saved-template snapshot that otherwise only gets written on page load and on refresh —
   * leaving it stale would keep the Landed Information dynamic headers on the previous
   * template while the row data is already rebuilt from the new one. When the template
   * fetch fails the snapshot is cleared rather than kept, so no headers are shown against
   * rows that no longer belong to them.
   * @param selected costing template chosen in the dropdown.
   */
  const handleTemplateSelect = async (selected: TemplateWithCostingInfo): Promise<void> => {
    const res = await dispatch(getProductCostingTemplateDetailsByIds(selected._id, devOrderId, { colourWayToggle, sizeLengthToggle }));
    if (res) {
      setInternalState((prev) => ({
        ...prev,
        currSelectedTemplateDetails: res as CostingTemplateDetail,
      }));
      await handleAdditonalModule(res as LandedTemplateArg, additionalState);
      await handleAdditonalComponentModule(res as LandedTemplateArg, additionalComponentState);
      handleSpecificationRequirementModule(res as LandedTemplateArg);
      if (incoterms && selected) {
        const dcList: LandedInfoStateEntry[] =
          landedInfoStates.length > 0
            ? landedInfoStates
            : filteredDistributionCentres.map(
                (dc) =>
                  ({
                    distributionCentre: dc,
                    landedInfo: { landedInfoColumnArr: [], landedInfoArr: [], currency: null, targetCost: '' },
                    landedExchangeRateInformation: {},
                  } as LandedInfoStateEntry),
              );
        const savedCombinationRows = productid
          ? getSavedLandedCombinationRows((dcList.length > 0 ? dcList : [landedInfoState]) as unknown as LandedDistributionCentreState[])
          : [];
        if (dcList.length > 0) {
          for (let i = 0; i < dcList.length; i++) {
            await handleLandedInfoModule(res as LandedTemplateArg, dcList[i] as LandedInfoStateEntry, undefined, undefined, i, savedCombinationRows);
          }
        } else {
          await handleLandedInfoModule(
            res as LandedTemplateArg,
            landedInfoState as unknown as LandedInfoStateEntry,
            undefined,
            undefined,
            undefined,
            savedCombinationRows,
          );
        }
      }
    }
    setProductCostingState((state) => {
      const next: ProductCostingState = cloneDeep(state);
      set(next, commonPath().SELECTED_TEMPLATE, selected);
      set(next, commonPath().LANDED_INFORMATION_COLUMNS, (res as TemplateWithCostingInfo)?.landedInfo);
      return next;
    });
  };

  const updateRequiredSection = async (value: boolean, key: string): Promise<void> => {
    const copyIntialLandedSettinginfo = cloneDeep(initialLandedInfoState);
    const colourWayToggleState = key === 'colourWayToggle' ? value : colourWayToggle;
    const sizeLengthToggleState = key === 'sizeLengthToggle' ? value : sizeLengthToggle;
    if (averageCostingActive) {
      await handleAverageCosting(colourWayToggleState, sizeLengthToggleState);
    } else if (detailedCostingActive) {
      await handleQuantities(colourWayToggleState, sizeLengthToggleState);
      await renderDetailedCostingArr(detailedCostingState?.detailedCosting?.bomTypeArr as BomListArg, true, colourWayToggleState, sizeLengthToggleState);
    } else {
      await handleAverageCosting(colourWayToggleState, sizeLengthToggleState);
    }
    if (costingTemplateDetail) {
      const res = await dispatch(
        getProductCostingTemplateDetailsByIds(costingTemplateDetail?._id, devOrderId, {
          colourWayToggle: colourWayToggleState,
          sizeLengthToggle: sizeLengthToggleState,
        }),
      );
      if (res) {
        await handleAdditonalModule(res as LandedTemplateArg, additionalState);
        await handleAdditonalComponentModule(res as LandedTemplateArg, additionalComponentState);
      handleSpecificationRequirementModule(res as LandedTemplateArg);
        setInternalState((prev) => ({ ...prev, currSelectedTemplateDetails: res as CostingTemplateDetail }));
        if (landedInfoStates.length > 0) {
          for (let i = 0; i < landedInfoStates.length; i++) {
            const freshDcState = {
              ...landedInfoStates[i],
              landedInfo: { landedInfoColumnArr: [], landedInfoArr: [], currency: null, targetCost: '' },
            } as LandedInfoStateEntry;
            await handleLandedInfoModule(res as LandedTemplateArg, freshDcState, [] as LandedGrandTotalArg, undefined, i);
          }
        } else {
          await handleLandedInfoModule(res as LandedTemplateArg, copyIntialLandedSettinginfo as unknown as LandedInfoStateEntry, [] as LandedGrandTotalArg);
        }
      }
    }
  };

  useEffect(() => {
    if (!productid && !isGlobalUrl && Array.isArray(productColours) && productColours.length === 0) {
      setActivateColSizeLengthToggle((prev) => ({ ...prev, colourWayToggle: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productColours?.length, productid]);

  const hasTriggeredInitialColourway = useRef(false);
  useEffect(() => {
    if (
      !productid &&
      !isGlobalUrl &&
      !hasTriggeredInitialColourway.current &&
      (productColours?.length ?? 0) > 0 &&
      (initialOptionsState?.colourwayOptionsArr?.length ?? 0) > 0
    ) {
      hasTriggeredInitialColourway.current = true;
      setActivateColSizeLengthToggle((prev) => ({ ...prev, colourWayToggle: true }));
      updateRequiredSection(true, 'colourWayToggle');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOptionsState?.colourwayOptionsArr?.length, productColours?.length]);

  const getSupplierFactoryOptions = useMemo<FactoryOption[]>(() => {
    if (supplierFactories?.length) {
      const allSupplierFact = supplierFactories?.filter((vl) => String(vl?.supplierId) === supplierId?._id);
      const factories = allSupplierFact?.flatMap((supplier) => supplier?.factories || []);
      return factories?.filter((factory) => productDetails?.supplierFactories?.includes(factory?._id as string));
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierFactories?.length, productDetails?.supplierFactories?.length, supplierId?._id]);

  const incotermsOptions = useMemo<NamedOption[]>(() => {
    return activeIncotermsList || [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIncotermsList?.length]);

  const defaultIncoterm = useMemo(() => {
    return activeIncotermsList?.find((incoterm) => incoterm.isActiveIncoterms === true) || null;
  }, [activeIncotermsList]);

  useEffect(() => {
    if (!incoterms && defaultIncoterm && !isDisabled) {
      handleDropDown(defaultIncoterm, productInfoFieldPath().SELECTED_INCOTERMS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultIncoterm, incoterms, isDisabled, handleDropDown]);

  useEffect(() => {
    if (!productid && !supplierId && suppliers?.length === 1 && !isDisabled) {
      onChangeSupplier(suppliers[0] as SupplierOption);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suppliers?.length, supplierId, productid, isDisabled]);

  useEffect(() => {
    if (!productid && !factoryId && getSupplierFactoryOptions?.length === 1 && !isDisabled) {
      handleDropDown(getSupplierFactoryOptions[0], productInfoFieldPath().SELECTED_FACTORY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getSupplierFactoryOptions?.length, factoryId, productid, isDisabled]);

  useEffect(() => {
    if (!productid && !productTemplate && costingTemplateList?.length === 1 && !isDisabled) {
      handleTemplateSelect(costingTemplateList[0] as TemplateWithCostingInfo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costingTemplateList?.length, productTemplate, productid, isDisabled]);

  const hasBuiltInitialLanded = useRef(false);
  useEffect(() => {
    if (!productid && !isGlobalUrl && !hasBuiltInitialLanded.current && productTemplate && incoterms) {
      hasBuiltInitialLanded.current = true;
      handleLandedInformationChanges(productTemplate, incoterms as IncotermArg);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productTemplate, incoterms, productid]);

  /**
   * Renders one dynamic Costing Information field by its declared type.
   * @param item the template's non-default costingInfo entry to render.
   * @returns the control bound to that field's stored value.
   */
  const renderExtraCostingField = (item: CostingInfoField): React.JSX.Element => {
    const fieldValue = internalState.costingInfoValues?.[item._id as string];
    const fieldDisabled = !CoastingAddEditRights || isDisabled;
    const fieldPlaceholder = (item.aliasDisplayName || item.title) as string;

    if (item.type === 'datePicker') {
      return (
        <div className="costing-datepicker w-100">
          <div className="valueProp">
            <div className="input-button-container custom-calender">
              <DatePicker
                dateFormat="dd-MMM-yyyy"
                popperPlacement="bottom-end"
                name="selectedDate"
                selected={(fieldValue as Date | null) || null}
                onChange={(date: unknown) => handleCostingInfoChange(item._id as string, date)}
                closeOnScroll
                portalId="root-portal"
                customInput={<CostingDateInput />}
                calendarContainer={CostingCalendarContainer}
                disabled={fieldDisabled}
                shouldCloseOnSelect={false}
              />
            </div>
          </div>
        </div>
      );
    }

    if (item.type === 'textArea') {
      return (
        <NewTextArea
          rows={1}
          className="costing-info-textarea"
          value={(fieldValue as string) || ''}
          onChange={(e) => handleCostingInfoChange(item._id as string, e.target.value)}
          disabled={fieldDisabled}
          placeholder={fieldPlaceholder}
        />
      );
    }

    return (
      <NewInput
        type="text"
        value={(fieldValue as string) || ''}
        onChange={(e) => handleCostingInfoChange(item._id as string, e.target.value)}
        disabled={fieldDisabled}
        placeholder={fieldPlaceholder}
      />
    );
  };

  return (
    <>
      <Accordion defaultActiveKey="productInfo">
        <Card className="costing-info-card mb-16">
          <Card.Header className="costing-info-header">
            <Accordion.Toggle as={Button} variant="" eventKey="productInfo" className="btn-toggle costing-info-title">
              {t('DEVLOPMENT_ORDER.PRODUCT_COSTING.COSTING_INFORMATION')}
            </Accordion.Toggle>
            {!isSupplierView && (
              <div className="costing-info-toggles d-flex align-items-center">
                <div className="d-flex align-items-center">
                  <div className="switch-common d-flex design-switch">
                    <input
                      id="toggleColourway"
                      type="checkbox"
                      className="form-check-input mt-0"
                      checked={colSizeLengthToggleState?.colourWayToggle || false}
                      name="colourWayToggle"
                      onChange={(e) => {
                        setActivateColSizeLengthToggle((prev) => ({ ...prev, colourWayToggle: e.target.checked }));
                        updateRequiredSection(e.target.checked, 'colourWayToggle');
                      }}
                      disabled={productColours?.length === 0 || statusState}
                    />
                    <label className="text-nowrap design-title text-black mt-0 mb-0" htmlFor="toggleColourway">
                      {t('DEVLOPMENT_ORDER.PRODUCT_COSTING.COLOURWAY')}
                    </label>
                  </div>
                </div>
                <div className="d-flex align-items-center">
                  <div className="switch-common d-flex design-switch">
                    <input
                      id="toggleSizeLength"
                      type="checkbox"
                      className="form-check-input mt-0"
                      checked={colSizeLengthToggleState?.sizeLengthToggle || false}
                      name="sizeLengthToggle"
                      onChange={(e) => {
                        setActivateColSizeLengthToggle((prev) => ({ ...prev, sizeLengthToggle: e.target.checked }));
                        updateRequiredSection(e.target.checked, 'sizeLengthToggle');
                      }}
                      disabled={productSizes?.length === 0 || onLoadDisable || statusState}
                    />
                    <label className="text-nowrap design-title text-black mt-0 mb-0" htmlFor="toggleSizeLength">
                      {`Size and ${lengthLabel}`}
                    </label>
                  </div>
                </div>
              </div>
            )}
          </Card.Header>
          <Accordion.Collapse eventKey="productInfo">
            <Card.Body className="has-controls">
              <div className="row form-row align-items-start row-equal-cols flex-nowrap">
                {/* Reference */}
                <div className="col">
                  <NewInput
                    label={getCostingInfoLabel(0, t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_INFO_TABLE.REFERENCE'))}
                    type="text"
                    value={(reference as string) || ''}
                    onChange={(e) => handleChange(e.target.value, productInfoFieldPath().REFERENCE)}
                    placeholder={getCostingInfoPlaceholder(
                      0,
                      'costing:DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_INFO_TABLE.ENTER',
                      t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_INFO_TABLE.COSTING_REFERENCE'),
                    )}
                    disabled={!CoastingAddEditRights || isDisabled || onLoadDisable}
                  />
                </div>

                {/* Supplier */}
                <div className="col">
                  <div className={`common-react-select select-new${get(globalError, 'productInfo.supplierId', '') ? ' cp-field-error' : ''}`}>
                    <div className="field-name">
                      {getCostingInfoLabel(1, t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_INFO_TABLE.SUPPLIER'))}
                      <span className="asterisk">*</span>
                    </div>
                    {isDisabled ? (
                      <div className="form-control border-none">{supplierId?.name}</div>
                    ) : (
                      <NewSelectMultiSelect
                        options={suppliers ?? []}
                        onChange={(selected: SupplierOption | null) => {
                          onChangeSupplier(selected);
                          if (get(globalError, 'productInfo.supplierId', '')) {
                            updateError(errorpath().PRODUCTINFO.ERROR_SUPPLIER);
                          }
                        }}
                        getOptionLabel={(option: SupplierOption) => option.name ?? ''}
                        getOptionValue={(option: SupplierOption) => option.name as string}
                        value={supplierId}
                        isDisabled={isDisabled}
                        placeholder={getCostingInfoPlaceholder(
                          1,
                          'costing:DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_INFO_TABLE.SELECT',
                          t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_INFO_TABLE.SELECT_SUPPLIER'),
                        )}
                        menuPosition="fixed"
                      />
                    )}
                  </div>
                </div>

                {/* Factory */}
                <div className="col">
                  <div className={`common-react-select select-new${get(globalError, 'productInfo.factoryId', '') ? ' cp-field-error' : ''}`}>
                    <div className="field-name">{getCostingInfoLabel(2, t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_INFO_TABLE.FACTORY'))}</div>
                    {isDisabled ? (
                      <div className="form-control border-none">{factoryId?.factoryName}</div>
                    ) : (
                      <NewSelectMultiSelect
                        options={getSupplierFactoryOptions ?? []}
                        onChange={(selected: FactoryOption | null) => {
                          handleDropDown(selected, productInfoFieldPath().SELECTED_FACTORY);
                          if (get(globalError, 'productInfo.factoryId', '')) {
                            updateError(errorpath().PRODUCTINFO.ERROR_FACTORY);
                          }
                        }}
                        getOptionLabel={(option: FactoryOption) => option.factoryName ?? ''}
                        getOptionValue={(option: FactoryOption) => option._id as string}
                        value={factoryId}
                        isDisabled={isDisabled || onLoadDisable}
                        placeholder={getCostingInfoPlaceholder(
                          2,
                          'costing:DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_INFO_TABLE.SELECT',
                          t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_INFO_TABLE.SELECT_FACTORY'),
                        )}
                        menuPosition="fixed"
                      />
                    )}
                  </div>
                </div>

                {/* Costing Template */}
                <div className="col">
                  <div className={`common-react-select select-new${get(globalError, 'productTemplate', '') ? ' cp-field-error' : ''}`}>
                    <div className="field-name">
                      {getCostingInfoLabel(3, t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_INFO_TABLE.SELECT_COSTING_TEMPLATE'))}
                      <span className="asterisk">*</span>
                    </div>
                    <NewSelectMultiSelect
                      options={costingTemplateList ?? []}
                      getOptionValue={(option: TemplateWithCostingInfo) => option.name as string}
                      getOptionLabel={(option: TemplateWithCostingInfo) => option.name ?? ''}
                      onChange={(selected: TemplateWithCostingInfo | null) => {
                        productid ? openAlertModal(selected) : handleTemplateSelect(selected as TemplateWithCostingInfo);
                        if (get(globalError, 'productTemplate', '')) {
                          updateError(errorpath().SELECT_TEMPLATE);
                        }
                      }}
                      isDisabled={isSupplier || onLoadDisable || isDisabled || !CoastingAddEditRights}
                      value={productTemplate}
                      placeholder={getCostingInfoPlaceholder(
                        3,
                        'costing:DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_INFO_TABLE.SELECT',
                        t('DEVLOPMENT_ORDER.PRODUCT_COSTING_INDEX.SELECT_COSTING_TEMPLATE'),
                      )}
                      menuPosition="fixed"
                    />
                  </div>
                </div>

                {/* Currency */}
                <div className="col col-w-currency">
                  <div className={`common-react-select select-new${get(globalError, 'productInfo.currency', '') ? ' cp-field-error' : ''}`}>
                    <div className="field-name">{getCostingInfoLabel(4, t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_INFO_TABLE.CURRENCY'))}</div>
                    {isDisabled || currency ? (
                      <div className="border-none f-14 pl-1 pb-0">{currency}</div>
                    ) : (
                      <NewSelectMultiSelect
                        options={currencyOptionsArr ?? []}
                        value={convertSelectLabel(currency, true)}
                        onChange={(selected: LabelValueOption | null) => {
                          handleDropDown(selected?.value, productInfoFieldPath().SELECTED_CURRENCY);
                          if (get(globalError, 'productInfo.currency', '')) {
                            updateError(errorpath().PRODUCTINFO.ERROR_CURRENCY);
                          }
                        }}
                        isDisabled={isDisabled || onLoadDisable || !CoastingAddEditRights}
                        placeholder={getCostingInfoPlaceholder(
                          4,
                          'costing:DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_INFO_TABLE.SELECT',
                          t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_INFO_TABLE.SELECT_CURRENCY'),
                        )}
                        menuPosition="fixed"
                      />
                    )}
                  </div>
                </div>

                {/* MOQ Needed */}
                <div className="col">
                  <NewInput
                    label={getCostingInfoLabel(5, t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_INFO_TABLE.MOQ_NEEDED'))}
                    type="text"
                    value={(moqNeeded as string) || ''}
                    placeholder={getCostingInfoPlaceholder(
                      5,
                      'costing:DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_INFO_TABLE.ENTER',
                      t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_INFO_TABLE.ENTER_MOQ_NEEDED'),
                    )}
                    onChange={(e) => {
                      const restrictedValue = restrictToTwoDecimals(e.target.value);
                      if (checkIsNumber(restrictedValue || 0)) {
                        handleChange(restrictedValue, productInfoFieldPath().MOQ_NEEDED);
                      }
                    }}
                    disabled={!CoastingAddEditRights || onLoadDisable || !supplierId || markAsCompleteState}
                  />
                </div>

                {/* Target Cost */}
                <div className="col">
                  <NewInput
                    label={getCostingInfoLabel(6, t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_INFO_TABLE.TARGET_COST'))}
                    invalid={Boolean(get(globalError, 'productInfo.targetCost', ''))}
                    type="text"
                    value={(targetCost as string) || ''}
                    placeholder={getCostingInfoPlaceholder(
                      6,
                      'costing:DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_INFO_TABLE.ENTER',
                      t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_INFO_TABLE.ENTER_TARGET_COST'),
                    )}
                    onChange={(e) => {
                      const restrictedValue = restrictToTwoDecimals(e.target.value);
                      if (checkIsNumber(restrictedValue || 0)) {
                        handleChange(restrictedValue, productInfoFieldPath().TARGET_COST);
                        if (get(globalError, 'productInfo.targetCost', '')) {
                          updateError(errorpath().PRODUCTINFO.ERROR_TARGETCOST);
                        }
                      }
                    }}
                    disabled={!CoastingAddEditRights || isDisabled}
                  />
                </div>

                {/* Incoterms */}
                <div className="col">
                  <div className={`common-react-select select-new${get(globalError, 'productInfo.incoterms', '') ? ' cp-field-error' : ''}`}>
                    <div className="field-name">
                      {getCostingInfoLabel(7, t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_INFO_TABLE.INCOTERMS'))}
                      <span className="asterisk">*</span>
                    </div>
                    {isDisabled ? (
                      <div className="form-control border-none">{incoterms?.name}</div>
                    ) : (
                      <NewSelectMultiSelect
                        options={incotermsOptions ?? []}
                        value={incoterms}
                        getOptionValue={(option: NamedOption) => option.name as string}
                        getOptionLabel={(option: NamedOption) => option.name as string}
                        onChange={(selected: NamedOption | null) => {
                          handleDropDown(selected, productInfoFieldPath().SELECTED_INCOTERMS);
                          if (get(globalError, 'productInfo.incoterms', '')) {
                            updateError(errorpath().PRODUCTINFO.ERROR_INCOTERMS);
                          }
                        }}
                        isDisabled={isDisabled || onLoadDisable || !CoastingAddEditRights}
                        placeholder={getCostingInfoPlaceholder(
                          7,
                          'costing:DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_INFO_TABLE.SELECT',
                          t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_INFO_TABLE.SELECT_INCOTERM'),
                        )}
                        menuPosition="fixed"
                      />
                    )}
                  </div>
                </div>

                {/* Shipment Method */}
                <div className="col">
                  <div className="common-react-select select-new">
                    <div className="field-name">{getCostingInfoLabel(8, t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_INFO_TABLE.SHIPMENT_METHOD'))}</div>
                    {isDisabled ? (
                      <div className="form-control border-none">{shipmentMethod}</div>
                    ) : (
                      <NewSelectMultiSelect
                        options={methodShipment ?? []}
                        value={convertSelectLabel(shipmentMethod, true)}
                        onChange={(selected: LabelValueOption | null) => {
                          handleDropDown(selected?.value, productInfoFieldPath().SELECTED_SHIPMENT_METHOD);
                        }}
                        isDisabled={isDisabled || onLoadDisable || !CoastingAddEditRights}
                        placeholder={getCostingInfoPlaceholder(
                          8,
                          'costing:DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_INFO_TABLE.SELECT',
                          t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_INFO_TABLE.SELECT_SHIPMENT_METHOD'),
                        )}
                        menuPosition="fixed"
                      />
                    )}
                  </div>
                </div>

                {/* Sample Ref */}
                <div className="col">
                  <div className="common-react-select select-new">
                    <div className="field-name">{getCostingInfoLabel(9, t('costing:DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_INFO_TABLE.SAMPLE_REF'))}</div>
                    <NewSelectMultiSelect
                      options={(initialOptionsState?.sampleRefOptionsArr as SampleRefOption[]) ?? []}
                      getOptionValue={(option: SampleRefOption) => option._id as string}
                      getOptionLabel={(option: SampleRefOption) => getSampleRefLabel(option)}
                      onChange={(selected: SampleRefOption | null) => {
                        handleSelectSampleRef(selected);
                      }}
                      value={sampleRef}
                      isClearable
                      placeholder={getCostingInfoPlaceholder(
                        9,
                        'costing:DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_INFO_TABLE.SELECT',
                        t('costing:DEVLOPMENT_ORDER.PRODUCT_COSTING_INDEX.SELECT_SAMPLE_REF'),
                      )}
                      menuPosition="fixed"
                      isDisabled={!CoastingAddEditRights || isSupplier || isDisabled || onLoadDisable}
                    />
                  </div>
                </div>
              </div>

              {/* Row 2 — dynamic extra fields (non-isDefault costingInfo items) */}
              {isCostingInfoActive && extraCostingInfo.length > 0 && (
                <div className="row form-row align-items-start row-equal-cols costing-info-extra-row">
                  {extraCostingInfo.map((item, extraIndex) => (
                    <div
                      key={item._id}
                      className={`col${item.type === 'textArea' && extraIndex === extraCostingInfo.length - 1 ? ' costing-info-extra-col--fill' : ''}`}
                    >
                      <div className="common-input-control input-new">
                        <div className="field-name">{item.aliasDisplayName || item.title}</div>
                        {renderExtraCostingField(item)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>

      {comState.showSupplierChangePopup && (
        <BombiixModal
          className="bombiix-modal--top"
          size="sm"
          show={comState.showSupplierChangePopup}
          title={t('ALERT_POPUP_VIEW.PRODUCT_COSTING.SUPPLIER_CHANGE.TITLE')}
          saveLabel={t('CONFIRMATION_POPUP.CONFIRM')}
          saveVariant="red"
          onSave={() => {
            updateSupplier();
          }}
          onClose={() =>
            setState((prevState) => ({
              ...prevState,
              showSupplierChangePopup: !prevState.showSupplierChangePopup,
              supplierSelected: null,
            }))
          }
        >
          <p className="costing-confirm__message">{t('ALERT_POPUP_VIEW.PRODUCT_COSTING.SUPPLIER_CHANGE.MESSAGE')}</p>
          <p className="costing-confirm__warning">{t('ALERT_POPUP_VIEW.PRODUCT_COSTING.SUPPLIER_CHANGE.DESCRIPTION')}</p>
        </BombiixModal>
      )}
      {comState.showCurrencyChangepopup && (
        <BombiixModal
          className="bombiix-modal--top"
          size="sm"
          show={comState.showCurrencyChangepopup}
          title={t('ALERT_POPUP_VIEW.PRODUCT_COSTING.CURRENCY.TITLE')}
          saveLabel={t('CONFIRMATION_POPUP.CONFIRM')}
          onSave={() => {
            updateCurrency();
          }}
          onClose={() =>
            setState((prevState) => ({
              ...prevState,
              showCurrencyChangepopup: !prevState.showCurrencyChangepopup,
              selectedCurrency: null,
            }))
          }
        >
          <p className="costing-confirm__message">{t('ALERT_POPUP_VIEW.PRODUCT_COSTING.CURRENCY.MESSAGE')}</p>
          <p className="costing-confirm__warning">{t('ALERT_POPUP_VIEW.PRODUCT_COSTING.CURRENCY.DESCRIPTION')}</p>
        </BombiixModal>
      )}
      {comState.showHideAlertPopup && (
        <BombiixModal
          className="bombiix-modal--top"
          size="sm"
          show={comState.showHideAlertPopup}
          title={t('ALERT_POPUP_VIEW.PRODUCT_COSTING.SELECT_TEMPLATE.TITLE')}
          saveLabel={t('CONFIRMATION_POPUP.CONFIRM')}
          saveVariant="red"
          onSave={() => {
            handleTemplateSelect(comState.selectedTemplate as TemplateWithCostingInfo);
            setState((prevState) => ({
              ...prevState,
              showHideAlertPopup: !prevState.showHideAlertPopup,
              selectedTemplate: null,
            }));
          }}
          onClose={() =>
            setState((prevState) => ({
              ...prevState,
              showHideAlertPopup: !prevState.showHideAlertPopup,
              selectedTemplate: null,
            }))
          }
        >
          <p className="costing-confirm__message">{t('ALERT_POPUP_VIEW.PRODUCT_COSTING.SELECT_TEMPLATE.MESSAGE')}</p>
          <p className="costing-confirm__warning">{t('ALERT_POPUP_VIEW.PRODUCT_COSTING.SELECT_TEMPLATE.DESCRIPTION')}</p>
        </BombiixModal>
      )}
    </>
  );
}

export default ProductInfoTable;

/** Calendar shell for the costing-info date fields; keeps the popper positioned relative. */
function CostingCalendarContainer({ className, children }: { className?: string | undefined; children?: React.ReactNode }): React.JSX.Element {
  return (
    <CalendarContainer className={className}>
      <div className="costing-calendar-anchor">{children}</div>
    </CalendarContainer>
  );
}

interface CostingDateInputProps {
  value?: string;
  onClick?: () => void;
  placeholder?: string;
}

/** Read-only date field with a calendar button, injected into react-datepicker as `customInput`. */
const CostingDateInput = forwardRef<HTMLButtonElement, CostingDateInputProps>(({ value, onClick, placeholder }, ref) => (
  <>
    <input type="text" value={value} disabled className="f-14" autoComplete="off" placeholder={placeholder} />{' '}
    <button type="button" className="btn-calender" onClick={onClick} ref={ref}>
      <CalendarIcon size={ICON_SIZE_FIELD} />
    </button>
  </>
));
